import React from 'react'
import { Form, Input, Button, Card, Typography, Space, App } from 'antd'
import { ArrowLeft, Save, Trash2 } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Layout } from 'antd'
import { Navigation } from '../../components/Navigation/Navigation'
import { useApi } from '../../services/ApiContext'
import { Supplier } from '../../types/ffe'

const { Content } = Layout
const { Title } = Typography
const { TextArea } = Input

export const SuppliersEditPage: React.FC = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { id } = useParams()
  const { message, modal } = App.useApp()
  const [loading, setLoading] = React.useState(false)
  const [initialValues, setInitialValues] = React.useState<Supplier | null>(
    null
  )

  const api = useApi()

  React.useEffect(() => {
    const loadSupplier = async () => {
      if (!id || id === 'new') return

      try {
        setLoading(true)
        const supplier = await api.getSupplier(id)
        if (supplier) {
          setInitialValues(supplier)
          form.setFieldsValue(supplier)
        }
      } catch (error) {
        console.error(error)
        message.error('Failed to load supplier')
        navigate('/app/suppliers')
      } finally {
        setLoading(false)
      }
    }

    loadSupplier()
  }, [id, form, navigate])

  const handleSubmit = async (values: Supplier) => {
    try {
      setLoading(true)
      if (id && id !== 'new') {
        await api.updateSupplier(id, values)
        message.success('Supplier updated successfully')
      } else {
        await api.createSupplier(values)
        message.success('Supplier created successfully')
      }
      navigate('/suppliers')
    } catch (error) {
      console.error(error)
      message.error('Failed to save supplier')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = () => {
    if (!id) return

    modal.confirm({
      title: 'Archive Supplier',
      content:
        'Are you sure you want to archive this supplier? This action cannot be undone.',
      okText: 'Archive',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await api.archiveSupplier(id)
          message.success('Supplier archived successfully')
          navigate('/app/suppliers')
        } catch (error) {
          console.error(error)
          message.error('Failed to archive supplier')
        }
      }
    })
  }

  return (
    <Layout className="min-h-screen">
      <Navigation />
      <Content className="p-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-between items-center mb-0">
            <div className="flex flex-col items-start gap-2 mb-4">
              <Link to="/app/suppliers">
                <Button
                  size="small"
                  type="text"
                  icon={<ArrowLeft className="h-4 w-4" />}
                >
                  Back to Supplier List
                </Button>
              </Link>
              <Title level={4}>
                {id === 'new' ? 'New Supplier' : 'Edit Supplier'}
              </Title>
            </div>
            <Space>
              {id !== 'new' && (
                <Button
                  danger
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={handleDelete}
                >
                  Archive
                </Button>
              )}
              <Button
                type="primary"
                icon={<Save className="h-4 w-4" />}
                onClick={() => form.submit()}
                loading={loading}
              >
                Save
              </Button>
            </Space>
          </div>

          <div className="space-y-6">
            <Card>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={initialValues || undefined}
              >
                <Form.Item
                  name="name"
                  label="Supplier Name"
                  rules={[
                    { required: true, message: 'Please enter supplier name' }
                  ]}
                >
                  <Input placeholder="Enter supplier name" />
                </Form.Item>

                <Title level={5} className="!mt-8 !mb-4">
                  Contact Information
                </Title>

                <Form.Item name={['contact', 'name']} label="Contact Name">
                  <Input placeholder="Enter contact name" />
                </Form.Item>

                <Form.Item name={['contact', 'email']} label="Email">
                  <Input placeholder="Enter email address" type="email" />
                </Form.Item>

                <Form.Item name={['contact', 'phone']} label="Phone">
                  <Input placeholder="Enter phone number" />
                </Form.Item>

                <Title level={5} className="!mt-8 !mb-4">
                  Additional Information
                </Title>

                <Form.Item name="address" label="Address">
                  <TextArea rows={3} placeholder="Enter address" />
                </Form.Item>

                <Form.Item name="website" label="Website">
                  <Input placeholder="Enter website URL" />
                </Form.Item>

                <Form.Item name="notes" label="Notes">
                  <TextArea rows={4} placeholder="Enter any additional notes" />
                </Form.Item>
              </Form>
            </Card>
          </div>
        </div>
      </Content>
    </Layout>
  )
}
